import { siteApiSlice } from "../../app/siteApiSlice";
import { SelectListItem } from "../../components/input/SelectList";
import { DraftViewModel, PromptViewModel } from "../scoreReport/scoreReportApiSlice";
import { StimulusMaterialViewModel } from "../stimulusMaterial/stimulusMaterialApiSlice";
import { CommentedEssayViewModel } from "../writing/drafting/draftingApiSlice";

export interface PeerReviewDraftViewModel {
	courseName: string;
	draft: DraftViewModel;
	commentedEssay: CommentedEssayViewModel;
	peerReview: PeerReviewViewModel;
	prompt: PromptViewModel;
	stimulusMaterials: StimulusMaterialViewModel[];
	signLanguageVideoURL: string;
	redirect: boolean;
}

export interface PeerReviewViewModel {
	id: number;
	author: string;
	reviewer: string;
	reviewerInitials: string;
	reviewerHasImage: boolean;
	reviewerId: number;
	isReviewed: boolean;
	canViewPage: boolean;
	enabled: boolean;
	canEditPage: boolean;
	canShowReviewer: boolean;
	canShowEssayAuthor: boolean;
	questions: PeerReviewQuestionViewModel[];
	canShowSentenceStarters: boolean;
	redirect: boolean;
}

export interface PeerReviewQuestionViewModel {
	id: number;
	question: string;
	answer: DraftPeerReviewQuestionAnswer;
	comment: string;
	isGeneral: boolean;
	sentenceStarters: string[];
}

export interface UpdatePeerReviewQuestionViewModel {
	id: number;
	answer: DraftPeerReviewQuestionAnswer;
	comment: string;
}

export interface PeerReviewUpdateModel {
	questionViewModels: UpdatePeerReviewQuestionViewModel[];
	reviewId: number;
	draftId: number;
	markAsDone: boolean;
}
export interface PeerReviewUpdateResult {
	status: string;
	model: PeerReviewViewModel;
}

export interface PeerReviewPrintResult {
	status: string;
	model: PeerReviewPrintViewModel[];
}

export interface PeerReviewPrintViewModel {
	promptName: string;
	courseName: string;
	date: string;
	draftNumber: number;
	author: string;
	reviewer: string;
	questions: PeerReviewPrintQuestionViewModel[];
}

export interface PeerReviewPrintQuestionViewModel {
	question: string;
	answer: DraftPeerReviewQuestionAnswer;
	comment: string;
	isGeneral: boolean;
}

export enum DraftPeerReviewQuestionAnswer {
	NoAnswer = "NoAnswer",
	Yes = "Yes",
	Somewhat = "Somewhat",
	No = "No",
}

export interface PeerReviewListViewModel {
	completedFilter?: boolean;
	reviewCount: number;
	reviews: PeerReviewListItemViewModel[];
}

export enum PeerReviewListItemStatus {
	NotStarted = "NotStarted",
	InProgress = "InProgress",
	Complete = "Complete",
}

export interface PeerReviewListItemUserViewModel {
	userId: number;
	name: string;
	initials: string;
	hasImage: boolean;
	anonymous: boolean;
}

export interface PeerReviewListItemViewModel {
	reviewId: number;
	author: PeerReviewListItemUserViewModel;
	reviewer: PeerReviewListItemUserViewModel;
	promptName: string;
	draftTitle: string;
	status: PeerReviewListItemStatus;
	requestDate?: string;
	reviewDate?: string;
}

export interface PeerReviewActivityViewModel {
	allWriters: SelectListItem[];
	activity: PeerReviewsActivityPerPromptViewModel[];
}

export interface PeerReviewStats {
	totalReviews: number;
	completedReviews: number;
	inProgressReviews: number;
	notStartedReviews: number;
}

export interface PeerReviewsActivityPerPromptViewModel extends PeerReviewStats {
	promptName: string;
	promptId: number;
	latestReviewDate?: Date;
	reviewRequests: PeerReviewActivityPerWriterViewModel[];
}

export interface PeerReviewUserInfoViewModel {
	id: number;
	hasImage: boolean;
	initials: string;
	formalFullName: string;
}

export interface PeerReviewActivityPerWriterViewModel extends PeerReviewStats {
	writer: PeerReviewUserInfoViewModel;
	requestDate?: Date;
	requestedDraftNumber?: number;
	requests: PeerReviewRequestViewModel[];
}

export interface PeerReviewRequestViewModel {
	draftId: number;
	reviewedDraftNumber: number;
	peerReviewId: number;
	reviewer: PeerReviewUserInfoViewModel;
	reviewStatus: PeerReviewListItemStatus;
	isReviewed: boolean;
	reviewDate?: Date;
}

export interface PeerReviewRequestInfoClassViewModel extends PeerReviewRequestViewModel {
	writer: PeerReviewUserInfoViewModel;
	requestDate?: Date;
}

export const peerReviewsApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		peerReviewIndex: build.query<PeerReviewDraftViewModel, number>({
			query: (id) => ({
				url: `/PeerReview`,
				params: { id },
			}),
			providesTags: ["PeerReview"],
			async onQueryStarted(id, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(siteApiSlice.util.invalidateTags(["Notifications"]));
				} catch (err) {}
			},
		}),
		peerReviewUpdate: build.mutation<PeerReviewUpdateResult, PeerReviewUpdateModel>({
			query: (payload) => ({
				url: `/PeerReview/Update`,
				method: "POST",
				body: payload,
			}),
			invalidatesTags: ["PeerReview", "ScoreReport"],
		}),
		peerReviewMarkAsRead: build.mutation<void, { id: number }>({
			query: (payload) => ({
				url: `/PeerReview/MarkViewedAjax`,
				method: "POST",
				body: payload,
			}),
			invalidatesTags: ["PeerReview", "Notifications"],
		}),
		getAssignedReviews: build.query<PeerReviewListViewModel, boolean | undefined>({
			query: (payload) => ({
				url:
					payload !== undefined
						? `/PeerReview/GetAssignedReviews?completed=${payload}`
						: `/PeerReview/GetAssignedReviews`,
				method: "GET",
			}),
			providesTags: ["CurrentCourse", "PeerReview"],
		}),
		getRequestedReviews: build.query<PeerReviewListViewModel, boolean | undefined>({
			query: (payload) => ({
				url:
					payload !== undefined
						? `/PeerReview/GetRequestedReviews?completed=${payload}`
						: `/PeerReview/GetRequestedReviews`,
				method: "GET",
			}),
			providesTags: ["CurrentCourse", "PeerReview"],
		}),
		peerReviewPrint: build.query<PeerReviewPrintResult, number[]>({
			query: (payload) => ({
				url: `/PeerReview/PeerReviewPrint`,
				method: "POST",
				body: payload,
			}),
			providesTags: ["PeerReview"],
		}),
		completedPeerReviewPrint: build.query<PeerReviewPrintResult, number>({
			query: (essayId) => ({
				url: `/PeerReview/CompletedPeerReviewPrintForDraft`,
				method: "POST",
				body: { essayId },
			}),
			providesTags: ["PeerReview"],
		}),
		getCourseReviews: build.query<PeerReviewActivityViewModel, void>({
			query: () => ({
				url: `/PeerReview/GetCourseReviews`,
			}),
			providesTags: ["PeerReview", "CurrentCourse"],
		}),
	}),
});

export const {
	usePeerReviewIndexQuery,
	usePeerReviewUpdateMutation,
	usePeerReviewMarkAsReadMutation,
	useGetAssignedReviewsQuery,
	useGetRequestedReviewsQuery,
	useLazyCompletedPeerReviewPrintQuery,
	useGetCourseReviewsQuery,
} = peerReviewsApiSlice;
